import { isKnownAgentPromptPreset, getAgentPromptPreset } from "./registry.js";
import { getAgentPersonality } from "./personalities.js";
import { AGENT_AUTONOMY_CHOICES, AGENT_EVIDENCE_MODES, AGENT_MODEL_PROFILE_IDS, AGENT_PROGRESS_MODES, } from "./compiler.js";
export const AGENT_PROMPT_SELECTION_NAMESPACE = "skills_agents";
export const AGENT_PROMPT_SELECTION_SETTING_KEY = "agent_prompt_selection";
const AUTONOMY_MODE_IDS = new Set(AGENT_AUTONOMY_CHOICES.map((c) => c.id));
const PROGRESS_MODE_IDS = new Set(AGENT_PROGRESS_MODES);
const EVIDENCE_MODE_IDS = new Set(AGENT_EVIDENCE_MODES);
const MODEL_PROFILE_IDS = new Set(Object.values(AGENT_MODEL_PROFILE_IDS));
function optionalString(value) {
    return typeof value === "string" && value.length > 0 ? value : undefined;
}
const SELECTION_STRING_KEYS = [
    "presetId",
    "presetVersion",
    "personalityId",
    "autonomyMode",
    "modelProfileId",
    "progressMode",
    "evidenceMode",
    "knowledgeProfileId",
    "systemPromptTemplateId",
];
/**
 * Validate a stored agent-prompt selection. Unknown preset/personality ids and
 * non-canonical modes are rejected; the returned normalized value carries only
 * known identifiers.
 */
export function validateAgentPromptSelection(raw) {
    const errors = [];
    if (raw === null || typeof raw !== "object" || Array.isArray(raw)) {
        return { valid: false, errors: ["agent prompt selection must be an object"] };
    }
    // The canonical stored shape wraps the selection in { agentPromptSelection };
    // accept the flat shape too so older/looser writers still validate.
    const container = raw;
    const hasEnvelope = Object.prototype.hasOwnProperty.call(container, "agentPromptSelection");
    if (hasEnvelope) {
        if (container.agentPromptSelection === null ||
            typeof container.agentPromptSelection !== "object" ||
            Array.isArray(container.agentPromptSelection)) {
            return {
                valid: false,
                errors: ["agentPromptSelection envelope must be an object"],
            };
        }
    }
    const value = (hasEnvelope ? container.agentPromptSelection : container);
    const normalized = {};
    for (const key of SELECTION_STRING_KEYS) {
        if (value[key] !== undefined && typeof value[key] !== "string") {
            errors.push(`${key} must be a string`);
        }
    }
    const presetId = optionalString(value.presetId);
    if (presetId !== undefined) {
        if (!isKnownAgentPromptPreset(presetId)) {
            errors.push(`Unknown preset id: ${presetId}`);
        }
        else {
            normalized.presetId = presetId;
            const preset = getAgentPromptPreset(presetId);
            const presetVersion = optionalString(value.presetVersion);
            // Fail closed: a stored version that does not match the registry's
            // current preset version is stale or forged — reject the selection.
            if (presetVersion !== undefined && presetVersion !== preset?.version) {
                errors.push(`Stale preset version for ${presetId}: ${presetVersion} (expected ${preset?.version ?? "?"})`);
            }
            else {
                normalized.presetVersion = preset?.version;
            }
        }
    }
    const personalityId = optionalString(value.personalityId);
    if (personalityId !== undefined) {
        if (!getAgentPersonality(personalityId)) {
            errors.push(`Unknown personality id: ${personalityId}`);
        }
        else {
            normalized.personalityId = personalityId;
        }
    }
    const autonomyMode = optionalString(value.autonomyMode);
    if (autonomyMode !== undefined) {
        if (!AUTONOMY_MODE_IDS.has(autonomyMode)) {
            errors.push(`Unknown autonomy mode: ${autonomyMode}`);
        }
        else {
            normalized.autonomyMode = autonomyMode;
        }
    }
    const modelProfileId = optionalString(value.modelProfileId);
    if (modelProfileId !== undefined) {
        if (!MODEL_PROFILE_IDS.has(modelProfileId)) {
            errors.push(`Unknown model profile id: ${modelProfileId}`);
        }
        else {
            normalized.modelProfileId = modelProfileId;
        }
    }
    const progressMode = optionalString(value.progressMode);
    if (progressMode !== undefined) {
        if (!PROGRESS_MODE_IDS.has(progressMode)) {
            errors.push(`Unknown progress mode: ${progressMode}`);
        }
        else {
            normalized.progressMode = progressMode;
        }
    }
    const evidenceMode = optionalString(value.evidenceMode);
    if (evidenceMode !== undefined) {
        if (!EVIDENCE_MODE_IDS.has(evidenceMode)) {
            errors.push(`Unknown evidence mode: ${evidenceMode}`);
        }
        else {
            normalized.evidenceMode = evidenceMode;
        }
    }
    const knowledgeProfileId = optionalString(value.knowledgeProfileId);
    if (knowledgeProfileId !== undefined)
        normalized.knowledgeProfileId = knowledgeProfileId;
    const systemPromptTemplateId = optionalString(value.systemPromptTemplateId);
    if (systemPromptTemplateId !== undefined) {
        if (!isKnownAgentPromptPreset(systemPromptTemplateId)) {
            errors.push(`Unknown system prompt template id: ${systemPromptTemplateId}`);
        }
        else {
            normalized.systemPromptTemplateId = systemPromptTemplateId;
        }
    }
    return { valid: errors.length === 0, errors, normalized };
}
//# sourceMappingURL=settings.js.map